import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const SubmissionFormBuilder = ({ formFields, onFormFieldsChange, keywords, onKeywordsChange, allowedFileTypes, onAllowedFileTypesChange, errors }) => {
  const { t } = useTranslation('conferences');
  const k = (key, opts) => t(`setupWizard.submissionForm.${key}`, opts);

  const [newField, setNewField] = useState({
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    options: []
  });
  const [newOption, setNewOption] = useState('');
  const [newKeyword, setNewKeyword] = useState('');

  const fieldTypeOptions = ['text', 'textarea', 'email', 'number', 'select', 'checkbox', 'file', 'date']?.map(value => ({
    value,
    label: k(`fieldType.options.${value}`)
  }));

  const defaultFields = [
    { label: k('defaultFields.title.label'), type: 'text', required: true, placeholder: k('defaultFields.title.placeholder') },
    { label: k('defaultFields.abstract.label'), type: 'textarea', required: true, placeholder: k('defaultFields.abstract.placeholder') },
    { label: k('defaultFields.keywords.label'), type: 'text', required: true, placeholder: k('defaultFields.keywords.placeholder') },
    { label: k('defaultFields.file.label'), type: 'file', required: true, placeholder: k('defaultFields.file.placeholder') },
    { label: k('defaultFields.authors.label'), type: 'text', required: true, placeholder: k('defaultFields.authors.placeholder') },
    { label: k('defaultFields.email.label'), type: 'email', required: true, placeholder: k('defaultFields.email.placeholder') }
  ];

  const handleAddField = () => {
    if (!newField?.label?.trim()) return;

    const field = {
      id: Date.now(),
      label: newField?.label,
      type: newField?.type,
      required: newField?.required,
      placeholder: newField?.placeholder,
      options: newField?.options,
      order: formFields?.length
    };

    onFormFieldsChange([...formFields, field]);
    setNewField({ label: '', type: 'text', required: false, placeholder: '', options: [] });
  };

  const handleRemoveField = (fieldId) => {
    onFormFieldsChange(formFields?.filter(f => f?.id !== fieldId));
  };

  const handleMoveField = (index, direction) => {
    const newFields = [...formFields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newFields?.length) return;

    [newFields[index], newFields[targetIndex]] = [newFields?.[targetIndex], newFields?.[index]];
    newFields?.forEach((field, idx) => field.order = idx);

    onFormFieldsChange(newFields);
  };

  const handleAddOption = () => {
    if (!newOption?.trim()) return;
    setNewField({
      ...newField,
      options: [...newField?.options, newOption?.trim()]
    });
    setNewOption('');
  };

  const handleRemoveOption = (option) => {
    setNewField({
      ...newField,
      options: newField?.options?.filter(o => o !== option)
    });
  };

  const handleUseDefaults = () => {
    const fields = defaultFields?.map((field, index) => ({
      id: Date.now() + index,
      ...field,
      order: index,
      options: []
    }));
    onFormFieldsChange(fields);
  };

  const handleAddKeyword = () => {
    const normalizedKeyword = newKeyword?.replace(/\s+/g, ' ')?.trim();
    if (!normalizedKeyword) return;

    const alreadyExists = (keywords || [])?.some(
      (keyword) => keyword?.toLowerCase() === normalizedKeyword?.toLowerCase()
    );
    if (alreadyExists) return;

    onKeywordsChange([...keywords, normalizedKeyword]);
    setNewKeyword('');
  };

  const handleRemoveKeyword = (keyword) => {
    onKeywordsChange(keywords?.filter(k2 => k2 !== keyword));
  };

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Accepted File Formats */}
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">{k('fileFormats.title')}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {k('fileFormats.subtitle')}
        </p>
        <div className="flex flex-wrap gap-3">
          {[
            { value: 'pdf', label: k('fileFormats.pdf'), icon: 'FileText' },
            { value: 'docx', label: k('fileFormats.docx'), icon: 'FileText' },
            { value: 'both', label: k('fileFormats.both'), icon: 'Files' }
          ]?.map((option) => (
            <button
              key={option?.value}
              type="button"
              onClick={() => onAllowedFileTypesChange?.(option?.value)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                allowedFileTypes === option?.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-foreground border-border hover:bg-muted'
              }`}
            >
              <Icon name={option?.icon} size={16} />
              {option?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-muted/50 rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">{k('quickStart.title')}</h3>
            <p className="text-sm text-muted-foreground">
              {k('quickStart.subtitle')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleUseDefaults}
            iconName="Zap"
            iconPosition="left"
          >
            {k('quickStart.button')}
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">{k('keywords.title')}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {k('keywords.subtitle')}
        </p>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder={k('keywords.placeholder')}
              value={newKeyword}
              onChange={(e) => setNewKeyword(e?.target?.value)}
              onKeyDown={(e) => {
                if (e?.key === 'Enter' || e?.key === ',') {
                  e?.preventDefault();
                  handleAddKeyword();
                }
              }}
            />
            <Button
              variant="outline"
              onClick={handleAddKeyword}
              iconName="Plus"
              disabled={!newKeyword?.trim()}
            >
              {k('add')}
            </Button>
          </div>

          {keywords?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {keywords?.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-md text-sm"
                >
                  {keyword}
                  <button
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="hover:text-error transition-colors duration-250"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {keywords?.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              {k('keywords.empty')}
            </p>
          )}
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 md:mb-6">{k('customFieldTitle')}</h3>

        <div className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={k('fieldLabel.label')}
              type="text"
              placeholder={k('fieldLabel.placeholder')}
              value={newField?.label}
              onChange={(e) => setNewField({ ...newField, label: e?.target?.value })}
              required
            />

            <Select
              label={k('fieldType.label')}
              options={fieldTypeOptions}
              value={newField?.type}
              onChange={(value) => setNewField({ ...newField, type: value, options: [] })}
              required
            />
          </div>

          <Input
            label={k('placeholderText.label')}
            type="text"
            placeholder={k('placeholderText.placeholder')}
            value={newField?.placeholder}
            onChange={(e) => setNewField({ ...newField, placeholder: e?.target?.value })}
          />

          {newField?.type === 'select' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {k('dropdownOptions.label')}
              </label>
              <div className="flex gap-2 mb-3">
                <Input
                  type="text"
                  placeholder={k('dropdownOptions.placeholder')}
                  value={newOption}
                  onChange={(e) => setNewOption(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddOption())}
                />
                <Button
                  variant="outline"
                  onClick={handleAddOption}
                  iconName="Plus"
                  disabled={!newOption?.trim()}
                >
                  {k('add')}
                </Button>
              </div>

              {newField?.options?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newField?.options?.map((option, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-md text-sm"
                    >
                      {option}
                      <button
                        onClick={() => handleRemoveOption(option)}
                        className="hover:text-error transition-colors duration-250"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <Checkbox
            label={k('requiredField.label')}
            checked={newField?.required}
            onChange={(e) => setNewField({ ...newField, required: e?.target?.checked })}
            description={k('requiredField.description')}
          />

          <Button
            variant="default"
            onClick={handleAddField}
            iconName="Plus"
            iconPosition="left"
            disabled={!newField?.label?.trim() || (newField?.type === 'select' && newField?.options?.length === 0)}
          >
            {k('addField')}
          </Button>
        </div>
      </div>
      {formFields?.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base md:text-lg font-semibold text-foreground">
              {k('formPreview', { count: formFields?.length })}
            </h3>
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              iconPosition="left"
            >
              {k('previewForm')}
            </Button>
          </div>

          <div className="space-y-3 md:space-y-4">
            {formFields?.map((field, index) => (
              <div
                key={field?.id}
                className="bg-card rounded-lg border border-border p-4 md:p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-base font-semibold text-foreground">
                        {field?.label}
                      </h4>
                      {field?.required && (
                        <span className="text-error text-sm">*</span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium caption">
                        {fieldTypeOptions?.find(ft => ft?.value === field?.type)?.label}
                      </span>
                      {field?.required && (
                        <span className="px-2 py-1 bg-error/10 text-error text-xs font-medium caption">
                          {k('requiredBadge')}
                        </span>
                      )}
                    </div>

                    {field?.placeholder && (
                      <p className="text-sm text-muted-foreground mb-2">
                        {k('placeholderPrefix', { text: field?.placeholder })}
                      </p>
                    )}

                    {field?.options?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {field?.options?.map((option, optIndex) => (
                          <span
                            key={optIndex}
                            className="px-2 py-1 bg-muted text-muted-foreground text-xs"
                          >
                            {option}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveField(index, 'up')}
                      disabled={index === 0}
                      iconName="ChevronUp"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveField(index, 'down')}
                      disabled={index === formFields?.length - 1}
                      iconName="ChevronDown"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveField(field?.id)}
                      iconName="Trash2"
                      className="text-error hover:text-error"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {errors?.formFields && (
        <p className="text-sm text-error">{errors?.formFields}</p>
      )}
    </div>
  );
};

export default SubmissionFormBuilder;
